import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@bettercomp/ams-spa-auth-react';
import { amsPublicApiAccountSwitchListReachableAccounts } from 'service/amsApi';
import type { ReachableAccountResponse } from 'connectors/ams';
import { holdsAmsToken } from 'components/AmsAuth/hostOutcomes';
import { reportError } from 'utils/reporting';

const reachableAccountsQueryKey = ['ams', 'accounts', 'reachable'] as const;

type UseReachableAccountsReturn = {
  accounts: ReachableAccountResponse[];
  isLoading: boolean;
  /**
   * True when the list could not be loaded. Callers must render something for this — an empty
   * `accounts` is indistinguishable from a failed fetch otherwise, and a switcher that gates on
   * length would silently disappear on a 500 rather than telling the user it is broken.
   */
  isUnavailable: boolean;
};

/**
 * The accounts the signed-in caller may switch into, from `GET /ams/auth/accounts` — their home
 * account plus every target of an ACTIVE cross-account grant of theirs.
 *
 * Mirrors `useSwitchableWorkspaces`, including why it fetches eagerly: the rule for showing the
 * control is "more than one reachable account", so the answer is the list itself and it has to be
 * in hand before the control renders.
 *
 * Unlike the workspace list there is no `forbidden` case to swallow. That route is
 * `@grant_required(WORKSPACE_SWITCH)`; this one is deliberately gated by no permission code at all,
 * because a visitor's role comes from the target account's own catalog and may carry no AMS
 * permission — a gate would strand exactly the callers it exists for. The list is instead keyed on
 * the caller's identity, so a caller with no grants gets their home account alone rather than a
 * refusal.
 */
export function useReachableAccounts(): UseReachableAccountsReturn {
  const { session, outcome } = useAuthContext();

  const { data, isPending, error } = useQuery({
    queryKey: reachableAccountsQueryKey,
    queryFn: async () => {
      // One report path for both failure shapes, as in useSwitchableWorkspaces: an HTTP error
      // arrives as a structured `error` field, but a network fault or a throw from fetchWithRenewal
      // rejects instead, and React Query surfaces either to the caller as a bare boolean.
      try {
        const { data, error } = await amsPublicApiAccountSwitchListReachableAccounts();
        if (error) {
          // Attached as `cause` rather than interpolated: the generated client hands back the
          // thrown exception as `error` for a network-level failure, and an Error or DOMException
          // has no enumerable own properties, so JSON.stringify would render it "{}".
          throw new Error('Failed to list reachable accounts', { cause: error });
        }
        return data ?? [];
      } catch (err) {
        reportError(err);
        throw err;
      }
    },
    // Only while a token is held, as in useSwitchableWorkspaces.
    enabled: Boolean(session) && holdsAmsToken(outcome),
    // A stale list mis-states which accounts the caller may enter, and the switch itself is the
    // authority anyway — every unreachable target answers 404 rather than trusting this cache.
    staleTime: 0,
    retry: false,
  });

  return {
    accounts: data ?? [],
    isLoading: Boolean(session) && isPending,
    isUnavailable: Boolean(error),
  };
}
